import { useState } from "react";

import Image from "next/image";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ChevronRightCircle, Menu, X } from "lucide-react";

import jetsmartLogo from "@repo/ui/assets/logos/jetsmart_logo.svg";
import { JetSmartButton } from "@repo/ui/components/common/forms/JetSmartButton";
import Benefits from "@repo/ui/components/Header/Benefits";
import CountrySelector from "@repo/ui/components/Header/CountrySelector/CountrySelector";
import Destinations from "@repo/ui/components/Header/Destinations";
import Profile from "@repo/ui/components/Header/Profile";
import Reservations from "@repo/ui/components/Header/Reservations";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import { Button } from "@repo/ui/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@repo/ui/components/ui/drawer";
import { Separator } from "@repo/ui/components/ui/separator";
import type { CultureNames } from "@repo/ui/types/cms";
import type { NavigationItem, PageFooterHeader } from "@repo/ui/types/cms/common";

import type { AuthUser } from "@repo/store/slices";

import UserDropdown from "./UserDropdown";

interface MobileMenuProps {
  menuItems: PageFooterHeader["header"]["navigationItems"];
  countryDropdown: PageFooterHeader["header"]["countryDropdown"];
  selectedCountry: PageFooterHeader["header"]["countryDropdown"][number] | null;
  openControl: () => void;
  loginButtonText: string;
  hideLoginButton: boolean;
  user?: AuthUser | null;
  onLogout?: () => void;
  onProfile?: () => void;
  onLogoClick?: () => void; // Add logo click handler
  cultureNames: CultureNames;
  headerContent?: PageFooterHeader["header"];
}

export default function MobileMenu({
  menuItems,
  countryDropdown,
  selectedCountry,
  openControl,
  loginButtonText,
  hideLoginButton,
  user,
  onLogout,
  onProfile,
  onLogoClick, // Add to destructure
  cultureNames,
  headerContent,
}: Readonly<MobileMenuProps>) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  function resolveMenuContent(menu: NavigationItem) {
    switch (menu.globalheadertab[0]?.name) {
      case "Destinations":
        return (
          <Destinations
            linkedItems={menu.linked_items || []}
            title={menu.title}
            subtitle={menu.subtitle}
          />
        );
      case "Reservation":
        return <Reservations linkedItems={menu.linked_items || []} title={menu.title} />;
      case "Benefits":
        return <Benefits linkedItems={menu.linked_items || []} title={menu.title} />;
      case "TripInformation":
        return (
          <Profile
            linkedItems={menu.linked_items || []}
            title={menu.title}
            subtitle={menu.subtitle}
          />
        );
      default:
        return (
          <div className="p-4 text-sm text-gray-600">No content available for {menu.name}</div>
        );
    }
  }

  return (
    <div
      className="border-b-brand-red border-b-4 bg-white overflow-y-auto"
      data-testid="mobile-menu-section"
    >
      <div
        className="container m-auto flex items-center justify-between px-4 py-3"
        data-testid="mobile-menu-container"
      >
        <div
          onClick={onLogoClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onLogoClick?.();
            }
          }}
          className="cursor-pointer"
          role="button"
          tabIndex={0}
          aria-label="Go to home"
          data-testid="mobile-menu-logo"
        >
          <Image
            src={jetsmartLogo}
            alt="Jetsmart"
            width={120}
            height={40}
            data-testid="mobile-header-jetsmart-logo"
          />
        </div>
        <div className="flex items-center justify-between gap-4" data-testid="mobile-menu-actions">
          {!hideLoginButton &&
            (user && onLogout && onProfile ? (
              <UserDropdown
                userName={user.firstName}
                onLogout={onLogout}
                onProfile={onProfile}
                headerContent={headerContent}
              />
            ) : (
              <JetSmartButton
                bgColor="bg-brand-blue"
                data-testid="mobile-header-login-btn"
                hoverColor="hover:bg-brand-red"
                textColor="text-white"
                borderColor=""
                iconAlt="Round Arrow"
                icon={<ChevronRightCircle />}
                iconClassName="w-5 h-5"
                className="capitalize"
                onClick={openControl}
              >
                {loginButtonText}
              </JetSmartButton>
            ))}
          <Drawer
            direction="right"
            noBodyStyles
            disablePreventScroll
            data-testid="mobile-menu-drawer"
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
          >
            <DrawerTrigger data-testid="mobile-menu-trigger-btn">
              <Menu size={24} color="#9E202C" strokeWidth={2.25} />
            </DrawerTrigger>
            <DrawerContent
              className="h-dvh !w-full bg-white md:hidden"
              data-testid="mobile-menu-content"
            >
              <div className="flex h-full flex-col" data-testid="mobile-menu-scrollable">
                <VisuallyHidden>
                  <DrawerTitle />
                </VisuallyHidden>
                <DrawerHeader className="shrink-0" data-testid="mobile-menu-header">
                  <div
                    className="flex flex-row justify-between align-middle"
                    data-testid="mobile-menu-header-content"
                  >
                    <div
                      onClick={onLogoClick}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onLogoClick?.();
                        }
                      }}
                      className="cursor-pointer"
                      role="button"
                      tabIndex={0}
                      aria-label="Go to home"
                      data-testid="mobile-menu-drawer-logo"
                    >
                      <Image
                        src={jetsmartLogo}
                        alt="Jetsmart"
                        width={120}
                        height={40}
                        data-testid="mobile-drawer-jetsmart-logo"
                      />
                    </div>
                    <DrawerClose asChild data-testid="mobile-menu-close-btn">
                      <X color="#8c8c8c" />
                    </DrawerClose>
                  </div>
                </DrawerHeader>
                <div
                  className="flex-1 overflow-y-auto px-3 pb-12 touch-auto"
                  data-vaul-no-drag
                  data-testid="mobile-menu-scrollable-content"
                >
                  <Accordion type="single" collapsible data-testid="mobile-navigation-accordion">
                    {menuItems.map((item: NavigationItem) => (
                      <AccordionItem
                        key={item.codename}
                        value={`item-${item.codename}`}
                        data-testid="mobile-navigation-item"
                      >
                        <AccordionTrigger
                          className="text-brand-blue data-[state=open]:text-brand-cyan px-2 text-base font-normal"
                          data-testid="mobile-navigation-trigger"
                        >
                          {item?.name}
                        </AccordionTrigger>
                        <AccordionContent className="p-0" data-testid="mobile-navigation-content">
                          {resolveMenuContent(item)}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {!user && (
                    <>
                      <Separator data-testid="mobile-navigation-separator1" />
                      <div className="py-4" data-testid="mobile-navigation-signup-section">
                        <Button
                          variant="outline"
                          className="w-full rounded-full border-[#173151] text-[#173151]"
                          data-testid="mobile-navigation-signup-btn"
                        >
                          {headerContent?.homepageLoginModal?.signUpButtonLabel || "Sign up here"}
                        </Button>
                      </div>
                    </>
                  )}
                  <Separator data-testid="mobile-navigation-separator2" />
                  <DrawerFooter data-testid="mobile-menu-footer" className="px-0 pt-4 pb-8">
                    <CountrySelector
                      countryDropdown={countryDropdown}
                      selectedCountry={selectedCountry}
                      cultureNames={cultureNames}
                      onSelect={() => setDrawerOpen(false)}
                    />
                  </DrawerFooter>
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </div>
  );
}
